// src/utils/UtilMethods.js
import React from "react"; // Import React
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
class UtilMethods {
  // Output format sample: 25 JUN 2026
  static formatDateDDMONYYY(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  // Output format sample: June 25, 2026
  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  // Output format sample: Jun 25, 2026
  static formatDateShort(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  // Output format sample: Jun 25, 2026 10:30:45 AM
  static formatDateTime(dateString) {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true, // Use 24-hour format; remove this line for 12-hour format
      })
    );
  }
  static toDateTimeLocalValue = (value) => {
    if (!value) return "";

    // already in datetime-local format, return as-is
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      return value.slice(0, 16);
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  };
  // Output format sample: 06/25/2026 10:30 AM
  static formatDateTimeDDMMYYYY(dateString) {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${formattedDate} ${formattedTime}`;
  }
  // Output format sample: 06/25/26 10:30
  static formatDateTimeDDMMYY(dateString) {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${formattedDate} ${formattedTime}`;
  }

  static canRegister(regDate, startDate) {
    const currentDate = new Date();
    return (
      new Date(regDate) <= currentDate && currentDate <= new Date(startDate)
    );
  }

  static validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    // Check if the email is valid and not equal to the invalid guest email
    return (
      emailRegex.test(email) && email !== process.env.REACT_APP_GUEST_EMAIL
    );
  };
  /**
   * Validates a password.
   *
   * @param {string} password - The password to validate.
   * @returns {string} - Returns "Y" if the password is valid, otherwise an error message.
   */
  static validatePassword = (password) => {
    // Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character
    //const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    // Password must be at least 8 characters long and contain at least one uppercase letter
    const passwordRegex = /^(?=.*[A-Z])[A-Za-z\d@$!_%*:?&-]{8,12}$/;
    // Password must be at least 8 characters long and contain at least one uppercase letter
    const pass = passwordRegex.test(password);
    return pass
      ? "Y"
      : "Password must be between 8 and 12 characters long and contain at least one uppercase letter";
  };
  // Phone validation function
  /**
   * Validates a phone number.
   *
   * @param {string} phone - The phone number to validate.
   * @returns {boolean} - Returns true if the phone number is valid and not equal to the guest phone number, otherwise false.
  

  static validatePhone(phone) {
    if (typeof phone !== "string" || !phone) {
      return false;
    }
    if (phone === process.env.REACT_APP_GUEST_PHONE) {
      return false;
    }
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    if (!phoneRegex.test(phone)) {
      return false;
    }
    return phone.length >= 8;
  }
 */
  static validatePhone(phone) {
    if (!phone || typeof phone !== "string") {
      return false;
    }

    if (phone === process.env.REACT_APP_GUEST_PHONE) {
      return false;
    }

    // Ensures proper country code + length per country
    if (!isValidPhoneNumber(phone)) {
      return false;
    }

    try {
      const parsed = parsePhoneNumber(phone);

      // Extra safety:
      // ✔ must be valid
      // ✔ must be possible
      // ✔ must contain country
      if (!parsed || !parsed.isValid() || !parsed.country) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }
  static formatTextWithLineBreaks(text) {
    // Ensure text is a string, default to an empty string if undefined or null
    const safeText = String(text || "");
    return safeText.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line.split(" ").map((word, wordIndex) => (
          <React.Fragment key={wordIndex}>
            {word}
            {wordIndex < line.split(" ").length - 1 && " "}
          </React.Fragment>
        ))}
        <br />
      </React.Fragment>
    ));
  }

  // Escape all HTML first, then selectively allow a small safe tag set used in event notes.
  static sanitizeLimitedHtml(text) {
    const safeText = String(text || "");

    const escaped = safeText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

    return escaped
      .replace(/&lt;\s*br\s*\/?\s*&gt;/gi, "<br/>")
      .replace(/&lt;\s*b\s*&gt;/gi, "<b>")
      .replace(/&lt;\s*\/\s*b\s*&gt;/gi, "</b>")
      .replace(/&lt;\s*strong\s*&gt;/gi, "<strong>")
      .replace(/&lt;\s*\/\s*strong\s*&gt;/gi, "</strong>")
      .replace(/&lt;\s*i\s*&gt;/gi, "<i>")
      .replace(/&lt;\s*\/\s*i\s*&gt;/gi, "</i>")
      .replace(/&lt;\s*em\s*&gt;/gi, "<em>")
      .replace(/&lt;\s*\/\s*em\s*&gt;/gi, "</em>")
      .replace(/\r\n|\n|\r/g, "<br/>");
  }

  static toSafeHtmlMarkup(text) {
    return { __html: UtilMethods.sanitizeLimitedHtml(text) };
  }

  static formatGroupMembers = (members = [], separator = "\n") => {
    if (!Array.isArray(members) || members.length === 0) {
      return "No active members found.";
    }

    const activeMembers = members.filter((member) => !member.deleted);

    if (activeMembers.length === 0) {
      return "No active members found.";
    }

    return activeMembers.map(
      (member) => this.formatGroupMember(member) // Call formatGroupMember with the member object
    );
  };

  static formatGroupMembersforXLS = (members = []) => {
    if (!Array.isArray(members) || members.length === 0) {
      return "No active members found.";
    }

    const activeMembers = members.filter((member) => !member.deleted);

    if (activeMembers.length === 0) {
      return "No active members found.";
    }

    let memberString;

    memberString = activeMembers
      .map(
        (member) =>
          `Name: ${member.firstName} ${member.lastName}, Age: ${member.age}, Gender: ${member.gender}`
      )
      .join("; ");

    return memberString;
  };
  static formatGroupMember = (member) => {
    return `\nName: ${member.firstName} ${member.lastName}, Age: ${member.age}, Gender: ${member.gender}`;
  };

  static convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }
  static mapEventFromDb = (event) => ({
    id: event?.eventID,
    name: event?.name || "",
    reg_date: event?.regDate || "",
    reg_end_date: event?.regEndDate || "",
    start_date: event?.startDate || "",
    reg_type: event?.onlineRegRequired ? "YES" : "NO",
    type: event?.type,
  });


  static log(level, message, ...optionalParams) {
    if (process.env.REACT_APP_LOG_MODE === "development") {
      const stack = new Error().stack;
      const callerLine = stack.split("\n")[2];
      const callerInfo = callerLine.match(/at (.+) \((.+):(\d+):(\d+)\)/);
      const callerDetails = callerInfo
        ? `${callerInfo[1]} at ${callerInfo[2]}:${callerInfo[3]}`
        : "unknown location";

      switch (level) {
        case "info":
          console.info(
            `[INFO] ${callerDetails} - ${message}`,
            ...optionalParams
          );
          break;
        case "warn":
          console.warn(
            `[WARN] ${callerDetails} - ${message}`,
            ...optionalParams
          );
          break;
        case "error":
          console.error(
            `[ERROR] ${callerDetails} - ${message}`,
            ...optionalParams
          );
          break;
        default:
          console.log(`[LOG] ${callerDetails} - ${message}`, ...optionalParams);
      }
    }
  }
  static error(message, ...optionalParams) {
    if (process.env.REACT_APP_LOG_MODE === "development") {
      console.error(message, ...optionalParams);
    }
  }

  static deepDiff(newObj, oldObj, parentKey = "") {
    const changes = [];

    // Ensure both newObj and oldObj are objects to avoid Object.keys errors
    const safeNewObj =
      typeof newObj === "object" && newObj !== null ? newObj : {};
    const safeOldObj =
      typeof oldObj === "object" && oldObj !== null ? oldObj : {};

    // Collect all unique keys
    const allKeys = new Set([
      ...Object.keys(safeNewObj),
      ...Object.keys(safeOldObj),
    ]);

    for (const key of allKeys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const newVal = safeNewObj[key];
      const oldVal = safeOldObj[key];

      if (Array.isArray(newVal) && Array.isArray(oldVal)) {
        // Compare arrays by JSON since element order matters
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          changes.push({ key: fullKey, oldValue: oldVal, newValue: newVal });
        }
      } else if (
        typeof newVal === "object" &&
        newVal !== null &&
        typeof oldVal === "object" &&
        oldVal !== null
      ) {
        // Recurse for nested objects
        changes.push(...UtilMethods.deepDiff(newVal, oldVal, fullKey));
      } else if (newVal !== oldVal) {
        // Primitive or type mismatch
        changes.push({ key: fullKey, oldValue: oldVal, newValue: newVal });
      }
    }

    return changes;
  }
  static stripHtmlTags(str) {
    return str
      ? str
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim()
      : "";
  }
}



export default UtilMethods;
